import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '@/store';
import Cookies from 'js-cookie';
import { decryptResponse, encrypt } from '@/utils/encryption';
type ApiState = {
  auth: {
    accessToken: string | null;
  };
};

type EncryptionExtraOptions = {
  skipEncryption?: boolean;
  skipDecryption?: boolean;
};

const configuredTimeout = Number(import.meta.env.VITE_PUBLIC_API_TIMEOUT);
const apiTimeout =
  Number.isFinite(configuredTimeout) && configuredTimeout > 0 ? configuredTimeout : 30000;

/** Adds the bearer token and parses JSON or plaintext responses from the API. */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: apiTimeout,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ApiState).auth.accessToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
  responseHandler: async (response: Response) => {
    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  },
});

const isNonJsonBody = (body: unknown): boolean =>
  (typeof FormData !== 'undefined' && body instanceof FormData) ||
  (typeof Blob !== 'undefined' && body instanceof Blob) ||
  (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
  (typeof ArrayBuffer !== 'undefined' && (body instanceof ArrayBuffer || ArrayBuffer.isView(body)));

/** Encrypts JSON requests and decrypts successful and failed API responses. */
const encryptedBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  EncryptionExtraOptions
> = async (args, api, extraOptions) => {
  let adjustedArgs = args;

  if (typeof args !== 'string') {
    const method = (args.method || 'GET').toUpperCase();
    const shouldEncrypt =
      args.body !== undefined &&
      method !== 'GET' &&
      !extraOptions?.skipEncryption &&
      !isNonJsonBody(args.body);

    if (shouldEncrypt) {
      try {
        const headers = new Headers(args.headers as HeadersInit | undefined);
        headers.set('Content-Type', 'application/json');

        adjustedArgs = {
          ...args,
          headers,
          body: encrypt(args.body),
        };
      } catch (error) {
        return {
          error: {
            status: 'CUSTOM_ERROR',
            error: error instanceof Error ? error.message : 'Request encryption failed',
          },
        };
      }
    }
  }

  const result = await rawBaseQuery(adjustedArgs, api, {});

  if (!extraOptions?.skipDecryption) {
    if (result.data !== undefined && result.data !== null) {
      try {
        result.data = decryptResponse(result.data);
      } catch {
        // Preserve plaintext responses while the backend transition is in progress.
      }
    }

    if (result.error?.data !== undefined && result.error.data !== null) {
      try {
        result.error.data = decryptResponse(result.error.data);
      } catch {
        // Preserve plaintext error responses while the backend transition is in progress.
      }
    }
  }

  return result;
};

const baseQueryWithReauth: typeof encryptedBaseQuery = async (args, api, extraOptions) => {
  const result = await encryptedBaseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    Cookies.remove('authToken');
    setTimeout(() => {
      if (window.location.pathname === '/login') return;
      window.location.href = '/login';
    }, 2000);
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: [
    'User',
    'Auth',
    'FeatureFlags',
    'Competitions',
    'Events',
    'PendingMarkets',
    'RollbackMarkets',
  ],
  endpoints: () => ({}),
});

// Export hooks for usage in functional components
export const {
  util: { getRunningQueriesThunk },
} = apiSlice;

export default apiSlice;
